import { Injectable, signal } from '@angular/core';

/**
 * Service to manage a global navigation overlay that covers the entire screen
 * including the header during route transitions.
 *
 * This overlay is created directly on the document body and uses a CSS class
 * on the html element to force all other elements to have lower z-index.
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationOverlayService {
  private overlayElement: HTMLDivElement | null = null;
  private readonly OVERLAY_ID = 'navigation-transition-overlay';
  private readonly OVERLAY_ACTIVE_CLASS = 'nav-overlay-active';

  /** Whether the overlay is currently visible */
  private _isVisible = signal(false);

  /** Read-only signal for overlay visibility */
  readonly isVisible = this._isVisible.asReadonly();

  /**
   * Show the navigation overlay
   * Creates a DOM element directly on the body to ensure it covers everything
   * Also adds a class to html element to lower z-index of fixed/sticky elements
   * Returns a promise that resolves when the overlay is fully painted
   */
  show(): Promise<void> {
    return new Promise((resolve) => {
      // Remove any existing overlay first
      this.removeOverlayElement();

      // Add class to html element to lower z-index of all fixed/sticky elements via CSS
      document.documentElement.classList.add(this.OVERLAY_ACTIVE_CLASS);

      // Create overlay element directly on the body
      this.overlayElement = document.createElement('div');
      this.overlayElement.id = this.OVERLAY_ID;

      // Set all styles at once
      this.overlayElement.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100vw;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.4);
        z-index: 2147483647;
        pointer-events: none;
      `;
      this.overlayElement.setAttribute('aria-hidden', 'true');

      // Append to body (at the very end, so it's on top of everything)
      document.body.appendChild(this.overlayElement);

      this._isVisible.set(true);

      // Wait for the next animation frame to ensure the overlay is painted
      // Use double requestAnimationFrame to ensure paint has completed
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });
  }

  /**
   * Hide the navigation overlay
   */
  hide(): void {
    this.removeOverlayElement();
    document.documentElement.classList.remove(this.OVERLAY_ACTIVE_CLASS);
    this._isVisible.set(false);
  }

  /**
   * Remove the overlay element from the DOM
   */
  private removeOverlayElement(): void {
    if (this.overlayElement && this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
      this.overlayElement = null;
    }
    // Also try to remove by ID in case of stale references
    const existingOverlay = document.getElementById(this.OVERLAY_ID);
    if (existingOverlay) {
      existingOverlay.remove();
    }
  }
}
